import {
	Alert,
	AlertDescription,
	Box,
	FormControl,
	FormLabel,
	Grid,
	Link,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	HStack,
	Stack,
	Switch,
	useToast,
	useDisclosure,
	Heading,
	Button,
} from "@chakra-ui/react";
import apiFetch from "@wordpress/api-fetch";
import { __ } from "@wordpress/i18n";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { BLOCKS } from "../../constants";
import { useDebounce } from "../../hooks";
import BlocksSkeleton from "../../skeleton/BlocksSkeleton";
import { BlockSettingsMap, SettingsMap } from "../../types";
import Filters from "./components/Filters";
import { CrownIcon, UpgradeNow } from "../../../blocks/components/icons/Icons";

const Blocks: React.FC = () => {
	const methods = useForm();
	const toast = useToast();
	const queryClient = useQueryClient();
	const settingsQuery = useQuery(["settings"], () =>
		apiFetch<SettingsMap>({
			path: "magazine-blocks/v1/settings",
		})
	);
	const [search, setSearch] = React.useState<string>("");
	const [sort, setSort] = React.useState<"newest" | "alphabetical">("newest");
	const debouncedSearch = useDebounce(search, 400);

	const save = useMutation(
		(data: BlockSettingsMap) =>
			apiFetch({
				path: "magazine-blocks/v1/settings",
				method: "POST",
				data: data,
			}),
		{
			onSuccess() {
				queryClient.invalidateQueries(["settings"]);
				toast({
					status: "success",
					isClosable: true,
					title: __("Blocks saved successfully", "magazine-blocks"),
				});
			},
			onError(e: Error) {
				toast({
					status: "error",
					isClosable: true,
					description: e.message,
					title: __("Error saving blocks", "magazine-blocks"),
				});
			},
		}
	);

	const onSubmit = (data: any) => {
		save.mutate(data);
	};

	const activateDeactivate = (
		mode: "activate" | "deactivate" = "activate"
	) => {
		return () => {
			const data = Object.entries(BLOCKS).reduce((acc, [key, value]) => {
				(acc as any)[key] = "activate" === mode ? true : false;
				return acc;
			}, {});
			methods.setValue("blocks", data);
			methods.handleSubmit(onSubmit)();
		};
	};

	const blocks = Object.entries(BLOCKS)
		.sort(([, { label: a }], [, { label: b }]) => {
			if (sort === "alphabetical") {
				if (a < b) {
					return -1;
				}
				if (a > b) {
					return 1;
				}
			}
			return 0;
		})
		.map(([key, val]) => ({
			...val,
			key: key,
			hidden:
				debouncedSearch &&
				!val.label.toLowerCase().includes(debouncedSearch),
		}));

	const { isOpen, onOpen, onClose } = useDisclosure();

	const allBlockHidden = blocks.every((b) => b.hidden);

	if (settingsQuery.isLoading) return <BlocksSkeleton />;

	return (
		<>
			<Stack px="6" mt="8">
				<Filters
					onActivateAll={activateDeactivate("activate")}
					onDeactivateAll={activateDeactivate("deactivate")}
					isAllActive={Object.values(
						settingsQuery.data?.blocks ?? {}
					).every((v) => v)}
					isAllInactive={Object.values(
						settingsQuery.data?.blocks ?? {}
					).every((v) => !v)}
					loading={save.isLoading}
					onSort={setSort}
					onSearch={setSearch}
					search={search}
					sort={sort}
				/>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<Grid
						gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
						gridGap="4"
						mt="6"
					>
						{allBlockHidden && (
							<Alert status="info" gridColumn="1/-1">
								<AlertDescription>
									{__(
										`Sorry, we couldn't find any blocks matching your search query. Please try a different search.`,
										"magazine-blocks"
									)}
								</AlertDescription>
							</Alert>
						)}
						{blocks.map(
							({
								label,
								icon: BlockIcon,
								//	@ts-ignore
								type: BlockType,
								key,
								hidden,
							}) => (
								<Controller
									key={key}
									name={`blocks.${key}`}
									control={methods.control}
									defaultValue={
										settingsQuery?.data?.blocks?.[
											key as keyof BlockSettingsMap
										]
									}
									render={({
										field: { onChange, value },
									}) => (
										<Box>
											{"pro" === BlockType ? (
												<Link
													onClick={onOpen}
													p="0"
													_hover={{
														textDecoration: "none",
														color: "inherit",
														cursor: "pointer",
													}}
												>
													<FormControl
														display={
															hidden
																? "none"
																: "flex"
														}
														alignItems="center"
														border="1px"
														borderColor="gray.100"
														p="3"
														borderRadius="base"
														justifyContent="space-between"
														bgColor="white"
													>
														<HStack gap="3">
															<Box backgroundColor="#FAFBFF">
																<BlockIcon
																	fill="#D7D7D7"
																	w="7"
																	h="7"
																	m="14px"
																/>
															</Box>
															<Stack gap="2">
																<FormLabel
																	fontSize="sm"
																	fontWeight="semibold"
																	lineHeight="150%"
																	color="#383838"
																	opacity="0.5"
																	margin="0"
																	_hover={{
																		textDecoration:
																			"none",
																		cursor: "pointer",
																	}}
																>
																	{label}
																</FormLabel>
															</Stack>
														</HStack>
														<CrownIcon
															w="24px"
															h="24px"
														/>
														<Modal
															isOpen={isOpen}
															onClose={onClose}
															isCentered
														>
															<ModalOverlay />
															<ModalContent minW="540px">
																<ModalHeader></ModalHeader>
																<ModalCloseButton
																	_focusVisible={{
																		outline: 0,
																	}}
																	color="#A7A7A7"
																	fontSize="16px"
																/>
																<ModalBody p="0">
																	<Box
																		textAlign="center"
																		p="50px 60px 50px 60px"
																	>
																		<Box
																			display="flex"
																			justifyContent="center"
																		>
																			<UpgradeNow
																				w="150px"
																				h="130px"
																			/>
																		</Box>
																		<Heading
																			as="h2"
																			mb="12px"
																			mt="30px"
																			fontSize="26px"
																		>
																			{__(
																				"Unlock All Magazine Blocks",
																				"magazine-blocks"
																			)}
																		</Heading>
																		<Box
																			fontSize="16px"
																			color="#6B6B6B"
																		>
																			{__(
																				"This block is only available in the pro version. Please upgrade to a pro plan and unlock all blocks.",
																				"magazine-blocks"
																			)}
																		</Box>
																		<Button
																			p="16px"
																			as={
																				Link
																			}
																			bgColor="#2563EB"
																			mt="24px"
																			color="#FFFFFF"
																			lineHeight="16px"
																			w="100%"
																			_hover={{
																				bgColor:
																					"primary.600",
																				color: "#FFFFFF",
																				textDecoration:
																					"none",
																			}}
																			_focus={{
																				bgColor:
																					"#2563EB",
																				color: "#FFFFFF",
																				textDecoration:
																					"none",
																				outline: 0,
																			}}
																			href="https://wpblockart.com/magazine-blocks/pricing/?utm_source=magazine-blocks-plugin&utm_medium=magazine-blocks-plugin-dashboard&utm_content=upgrade-to-pro"
																			isExternal
																		>
																			{__(
																				"Upgrade to Pro",
																				"magazine-blocks"
																			)}
																		</Button>
																	</Box>
																</ModalBody>
															</ModalContent>
														</Modal>
													</FormControl>
												</Link>
											) : (
												<FormControl
													display={
														hidden ? "none" : "flex"
													}
													alignItems="center"
													border="1px"
													borderColor="gray.100"
													p="3"
													borderRadius="base"
													justifyContent="space-between"
													bgColor="white"
												>
													<HStack gap="3">
														<Box backgroundColor="primary.50">
															<BlockIcon
																fill="#690aa0"
																w="7"
																h="7"
																m="14px"
															/>
														</Box>
														<Stack gap="2">
															<FormLabel
																fontSize="sm"
																fontWeight="semibold"
																lineHeight="150%"
																color="gray.700"
																margin="0"
															>
																{label}
															</FormLabel>
														</Stack>
													</HStack>

													<Switch
														size="sm"
														isChecked={value}
														m="14px"
														colorScheme="primary"
														onChange={(e) => {
															onChange(
																e.target.checked
															);
															methods.handleSubmit(
																onSubmit
															)();
														}}
													/>
												</FormControl>
											)}
										</Box>
									)}
								/>
							)
						)}
					</Grid>
				</form>
			</Stack>
		</>
	);
};

export default Blocks;
